import React, { useMemo } from "react";

interface OrgChartProps {
  contacts: any[];
  onContactClick?: (contact: any) => void;
}

interface TreeNode {
  contact: any;
  children: TreeNode[];
}

const OrgChart: React.FC<OrgChartProps> = ({ contacts, onContactClick }) => {
  const [expandedNodes, setExpandedNodes] = React.useState<Set<string>>(
    new Set()
  );

  // Build tree structure from flat contact list
  const orgTree = useMemo(() => {
    // Filter contacts with hierarchy data
    const hierarchyContacts = contacts.filter(
      (c) => c.hierarchy_level !== null && c.hierarchy_level !== undefined
    );

    if (hierarchyContacts.length === 0) return [];

    // Create a map of contact ID to contact
    const contactMap = new Map<string, any>();
    hierarchyContacts.forEach((c) => contactMap.set(c.id, c));

    // Create tree nodes
    const nodeMap = new Map<string, TreeNode>();
    hierarchyContacts.forEach((c) => {
      nodeMap.set(c.id, { contact: c, children: [] });
    });

    // Build parent-child relationships
    const rootNodes: TreeNode[] = [];
    hierarchyContacts.forEach((c) => {
      const node = nodeMap.get(c.id)!;
      if (c.reports_to_contact_id && nodeMap.has(c.reports_to_contact_id)) {
        // Has a parent - add to parent's children
        const parentNode = nodeMap.get(c.reports_to_contact_id)!;
        parentNode.children.push(node);
      } else {
        // No parent or parent not in list - this is a root node
        rootNodes.push(node);
      }
    });

    // Sort children by last name
    const sortChildren = (node: TreeNode) => {
      node.children.sort((a, b) =>
        a.contact.last_name.localeCompare(b.contact.last_name)
      );
      node.children.forEach(sortChildren);
    };
    rootNodes.forEach(sortChildren);

    return rootNodes;
  }, [contacts]);

  const toggleNode = (contactId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      return next;
    });
  };

  const expandAll = () => {
    const allIds = new Set<string>();
    const collectIds = (node: TreeNode) => {
      if (node.children.length > 0) {
        allIds.add(node.contact.id);
        node.children.forEach(collectIds);
      }
    };
    orgTree.forEach(collectIds);
    setExpandedNodes(allIds);
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  const getRoleColor = (roleType?: string): string => {
    const colors: Record<string, string> = {
      decision_maker: "bg-red-100 border-red-300 text-red-800",
      champion: "bg-green-100 border-green-300 text-green-800",
      influencer: "bg-blue-100 border-blue-300 text-blue-800",
      blocker: "bg-orange-100 border-orange-300 text-orange-800",
      technical_evaluator: "bg-violet-100 border-violet-300 text-violet-800",
      budget_holder: "bg-yellow-100 border-yellow-300 text-yellow-800",
      end_user: "bg-slate-100 border-slate-300 text-slate-800",
    };
    return colors[roleType || ""] || "bg-gray-100 border-gray-300 text-gray-800";
  };

  const getRoleLabel = (roleType?: string): string => {
    if (!roleType) return "";
    return roleType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const renderNode = (node: TreeNode, depth = 0): React.ReactNode => {
    const { contact } = node;
    const hasChildren = node.children.length > 0;
    const isExpanded = expandedNodes.has(contact.id);
    const colorClass = getRoleColor(contact.role_type);

    return (
      <div key={contact.id} className="relative">
        {/* Node Card */}
        <div
          className="flex items-start gap-2 mb-2"
          style={{ marginLeft: `${depth * 40}px` }}
        >
          {/* Expand/Collapse Button */}
          {hasChildren && (
            <button
              onClick={() => toggleNode(contact.id)}
              className="mt-2 w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 flex-shrink-0"
            >
              {isExpanded ? (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              ) : (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              )}
            </button>
          )}
          {!hasChildren && <div className="w-6" />}

          {/* Contact Card */}
          <div
            className={`flex-1 border-2 rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow ${colorClass}`}
            onClick={() => onContactClick?.(contact)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="font-semibold text-sm">
                  {contact.first_name} {contact.last_name}
                </div>
                {contact.title && (
                  <div className="text-xs opacity-75 mt-1">
                    {contact.title}
                  </div>
                )}
                {contact.email && (
                  <div className="text-xs opacity-60 mt-1">
                    {contact.email}
                  </div>
                )}
              </div>
              {contact.role_type && (
                <div className="text-xs font-medium px-2 py-1 rounded bg-white/50 ml-2">
                  {getRoleLabel(contact.role_type)}
                </div>
              )}
            </div>
            {hasChildren && (
              <div className="text-xs opacity-60 mt-2">
                {node.children.length} direct report
                {node.children.length !== 1 ? "s" : ""}
              </div>
            )}
          </div>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="relative">
            {/* Connecting Line */}
            <div
              className="absolute top-0 bottom-0 w-px bg-gray-300"
              style={{ left: `${depth * 40 + 12}px` }}
            />
            <div>
              {node.children.map((child) => renderNode(child, depth + 1))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (orgTree.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-20 w-20 mb-4 text-blue-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <p className="text-xl font-semibold text-gray-900 mb-3">
          Build Your Org Chart
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md">
          <p className="text-sm text-blue-900 mb-2">
            <strong>To see the organizational hierarchy:</strong>
          </p>
          <ol className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
            <li>Edit a contact from the List view</li>
            <li>Scroll to the <strong>Organizational Hierarchy</strong> section</li>
            <li>Set the <strong>Reports To</strong> field (select their manager)</li>
            <li>Optionally set <strong>Hierarchy Level</strong> (0 = CEO, 1 = direct report, etc.)</li>
            <li>Save the contact - the org chart will update!</li>
          </ol>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-600">
          {orgTree.length} top-level {orgTree.length !== 1 ? "managers" : "manager"}
        </div>
        <div className="flex gap-2">
          <button
            onClick={expandAll}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Org Tree */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 overflow-x-auto">
        <div className="min-w-max">
          {orgTree.map((node) => renderNode(node))}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 flex flex-wrap gap-4 justify-center text-sm">
        {[
          "decision_maker",
          "champion",
          "influencer",
          "blocker",
          "technical_evaluator",
          "budget_holder",
          "end_user",
        ].map((role) => (
          <div key={role} className="flex items-center gap-2">
            <div
              className={`w-4 h-4 rounded border-2 ${getRoleColor(role)}`}
            />
            <span className="text-gray-700">{getRoleLabel(role)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrgChart;
